/* Offer Discovery - Coupons + Bank Offers drawer, plus the "best price"
   teaser near the PDP price. Fetches both lists from the backend (via the
   reviews App Proxy, see offers_controller.py), and separately checks
   /cart.js so an already-applied coupon shows a checkmark instead of an
   Apply button. */
(function () {
  var CHIP_COLORS = {
    'BEST VALUE': { bg: '#f7ecd3', color: '#8a6a1f' },
    'POPULAR': { bg: '#efe6fb', color: '#6b3fb8' },
    'NEW': { bg: '#e3edfb', color: '#1c5fc4' },
    'LIMITED': { bg: '#fbe7e5', color: '#c62f28' },
    'EXCLUSIVE': { bg: '#1c1c1c', color: '#ffffff' },
    'RECOMMENDED': { bg: '#e3f5e9', color: '#1a7f4b' },
  };

  var CARD_TYPE_LABELS = {
    credit: 'Credit Card',
    debit: 'Debit Card',
    credit_and_debit: 'Credit & Debit Card',
    emi: 'EMI',
  };

  var COPY_REVERT_MS = 2200;
  var TABS = ['coupons', 'bank-offers'];
  var CACHE_PREFIX = 'minaki_offer_cache_';
  var CACHE_TTL_MS = 5 * 60 * 1000;

  // Session-only cache, successes only - a transient backend error should
  // never get "stuck" for the rest of the session, so only a 2xx JSON
  // response with data.success is ever written here.
  function readOfferCache(key, productId, subtotal) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key + '_' + productId + '_' + subtotal);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed.cachedAt !== 'number' || !Array.isArray(parsed.items)) return null;
      if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
      return parsed.items;
    } catch (error) {
      return null;
    }
  }

  function writeOfferCache(key, productId, subtotal, items) {
    try {
      sessionStorage.setItem(
        CACHE_PREFIX + key + '_' + productId + '_' + subtotal,
        JSON.stringify({ items: items, cachedAt: Date.now() })
      );
    } catch (error) {
      // Storage full or unavailable (private browsing) - fall through to
      // fetching fresh every time, no different from having no cache at all.
    }
  }

  // Standard Shopify money_format formatter (cents in, formatted string out).
  function formatMoney(cents, format) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || '{{amount}}';

    function defaultTo(value, defaultValue) {
      return value == null || value !== value ? defaultValue : value;
    }
    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal = defaultTo(decimal, '.');
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      var cents2 = parts[1] ? decimal + parts[1] : '';
      return dollars + cents2;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  class OfferDiscovery extends HTMLElement {
    connectedCallback() {
      this.tiles = this.querySelectorAll('[data-offer-discovery-open]');
      this.backdrop = this.querySelector('[data-offer-discovery-backdrop]');
      this.panel = this.querySelector('[data-offer-discovery-panel]');
      this.closeBtn = this.querySelector('[data-offer-discovery-close]');
      this.tabButtons = this.querySelectorAll('[data-offer-tab]');
      this.tabIndicator = this.querySelector('[data-offer-tab-indicator]');
      this.liveEl = this.querySelector('[data-offer-discovery-live]');
      this.teaser = document.querySelector('[data-offer-teaser]');
      this.priceMrp = document.querySelector('[data-offer-price-mrp]');
      this.priceStrike = document.querySelector('[data-offer-price-strike]');
      this.priceFinal = document.querySelector('[data-offer-price-final]');

      this.cardTemplates = {
        coupons: this.querySelector('[data-offer-discovery-card-template="coupons"]'),
        'bank-offers': this.querySelector('[data-offer-discovery-card-template="bank-offers"]'),
      };

      this.moneyFormat = (this.dataset.moneyFormat || '{{amount}}').replace(/<[^>]*>/g, '').trim();
      this.currentPrice = parseInt(this.dataset.currentPrice, 10) || 0;
      this.endpoints = {
        coupons: this.dataset.couponsEndpoint,
        'bank-offers': this.dataset.bankOffersEndpoint,
      };

      this.data = { coupons: null, 'bank-offers': null };
      this.appliedCodes = null;
      this.activeTab = 'coupons';
      this.copyTimer = null;
      this._panelElsCache = {};

      var self = this;
      this.tiles.forEach(function (tile) {
        tile.addEventListener('click', function () { self.open(tile.dataset.tab); });
      });
      this.closeBtn.addEventListener('click', this.close.bind(this));
      this.backdrop.addEventListener('click', this.close.bind(this));
      this.tabButtons.forEach(function (tab) {
        tab.addEventListener('click', function () { self.switchTab(tab.dataset.offerTab); });
      });
      TABS.forEach(function (key) {
        self.panelEls(key).retry.addEventListener('click', function () { self.fetchTab(key); });
      });
      this.panel.addEventListener('keydown', this.onKeyDown.bind(this));

      this.updateTabUI();
      // Eager-load (not gated on the drawer opening) so the price teaser can
      // appear on page load.
      this.fetchTab('coupons');
      this.fetchTab('bank-offers');
      this.fetchCartDiscounts();
    }

    panelEls(key) {
      if (this._panelElsCache[key]) return this._panelElsCache[key];
      var body = this.querySelector('[data-offer-panel="' + key + '"]');
      var els = {
        body: body,
        skeleton: body.querySelector('[data-offer-discovery-skeleton]'),
        empty: body.querySelector('[data-offer-discovery-empty]'),
        error: body.querySelector('[data-offer-discovery-error]'),
        list: body.querySelector('[data-offer-discovery-list]'),
        retry: body.querySelector('[data-offer-discovery-retry]'),
      };
      this._panelElsCache[key] = els;
      return els;
    }

    open(tab) {
      if (tab) this.switchTab(tab);
      this.backdrop.setAttribute('aria-hidden', 'false');
      this.panel.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      this._lastFocused = document.activeElement;
      setTimeout(function (closeBtn) {
        closeBtn.focus();
      }, 320, this.closeBtn);
    }

    close() {
      this.backdrop.setAttribute('aria-hidden', 'true');
      this.panel.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this._lastFocused && this._lastFocused.focus) {
        this._lastFocused.focus();
      }
    }

    switchTab(tab) {
      if (TABS.indexOf(tab) === -1) return;
      this.activeTab = tab;
      this.updateTabUI();
    }

    updateTabUI() {
      var self = this;
      this.tabButtons.forEach(function (t) {
        var isActive = t.dataset.offerTab === self.activeTab;
        t.classList.toggle('is--active', isActive);
        t.setAttribute('aria-selected', String(isActive));
      });
      TABS.forEach(function (key) {
        self.panelEls(key).body.hidden = key !== self.activeTab;
      });
      if (this.tabIndicator) {
        var activeBtn = this.querySelector('[data-offer-tab="' + this.activeTab + '"]');
        if (activeBtn) {
          this.tabIndicator.style.width = activeBtn.offsetWidth + 'px';
          this.tabIndicator.style.transform = 'translateX(' + activeBtn.offsetLeft + 'px)';
        }
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;

      var focusables = this.panel.querySelectorAll(
        'button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    setState(key, state) {
      var els = this.panelEls(key);
      els.skeleton.hidden = state !== 'loading';
      els.empty.hidden = state !== 'empty';
      els.error.hidden = state !== 'error';
      els.list.hidden = state !== 'list';
    }

    fetchTab(key) {
      var endpoint = this.endpoints[key];
      if (!endpoint) return;

      var productId = this.dataset.productId;
      var subtotal = this.currentPrice / 100;

      var cached = readOfferCache(key, productId, subtotal);
      if (cached) {
        this.data[key] = cached;
        this.renderTab(key);
        this.updateTileSubtitle(key, cached.length);
        this.maybeUpdateTeaser();
        return;
      }

      this.setState(key, 'loading');
      var url = endpoint + '?product_id=' + encodeURIComponent(productId) + '&subtotal=' + encodeURIComponent(subtotal);

      var self = this;
      fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (response) {
          if (!response.ok) throw new Error('Request failed: ' + response.status);
          return response.json();
        })
        .then(function (data) {
          if (!data.success) throw new Error(data.message || 'Failed to load offers');
          var items = key === 'coupons' ? (data.coupons || []) : (data.bank_offers || []);
          self.data[key] = items;
          writeOfferCache(key, productId, subtotal, items);
          self.renderTab(key);
          self.updateTileSubtitle(key, items.length);
          self.maybeUpdateTeaser();
        })
        .catch(function () {
          self.data[key] = null;
          self.setState(key, 'error');
        });
    }

    fetchCartDiscounts() {
      var self = this;
      fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (cart) {
          var codes = {};
          if (cart && Array.isArray(cart.discount_codes)) {
            cart.discount_codes.forEach(function (dc) {
              if (dc.applicable) codes[String(dc.code).toUpperCase()] = true;
            });
          }
          self.appliedCodes = codes;
          if (self.data.coupons) self.renderTab('coupons');
        })
        .catch(function () {
          self.appliedCodes = {};
        });
    }

    renderTab(key) {
      var items = this.data[key];
      var els = this.panelEls(key);
      els.list.innerHTML = '';

      if (!items || !items.length) {
        this.setState(key, 'empty');
        return;
      }

      var self = this;
      items.forEach(function (offer) {
        var card = key === 'coupons' ? self.buildCouponCard(offer) : self.buildBankOfferCard(offer);
        els.list.appendChild(card);
      });
      this.setState(key, 'list');
    }

    updateTileSubtitle(key, count) {
      var el = this.querySelector('[data-tile-subtitle="' + key + '"]');
      if (!el || !count) return;
      var noun = key === 'coupons' ? 'coupon' : 'offer';
      el.textContent = count + ' ' + noun + (count === 1 ? '' : 's') + ' available';
    }

    applyChipStyle(chipEl, chip) {
      if (!chip) return;
      var colors = CHIP_COLORS[chip.toUpperCase()] || { bg: '#f2f0ea', color: '#6b6b6b' };
      chipEl.textContent = chip;
      chipEl.style.background = colors.bg;
      chipEl.style.color = colors.color;
      chipEl.hidden = false;
    }

    bindTerms(card, offer) {
      var termsWrap = card.querySelector('[data-card-terms-wrap]');
      if (!offer.terms || !offer.terms.length) return;
      termsWrap.hidden = false;
      var termsList = card.querySelector('[data-card-terms-list]');
      offer.terms.forEach(function (term) {
        var li = document.createElement('li');
        li.textContent = term;
        termsList.appendChild(li);
      });

      var toggleBtn = card.querySelector('[data-card-terms-toggle]');
      var termsPanel = card.querySelector('[data-card-terms-panel]');
      toggleBtn.addEventListener('click', function () {
        var expanded = toggleBtn.getAttribute('aria-expanded') === 'true';
        toggleBtn.setAttribute('aria-expanded', String(!expanded));
        termsPanel.classList.toggle('t4s-offer-discovery__terms-panel--open', !expanded);
      });
    }

    buildCouponCard(offer) {
      var node = this.cardTemplates.coupons.content.cloneNode(true);
      var card = node.querySelector('.t4s-offer-discovery__card');

      card.querySelector('[data-card-title]').textContent = offer.title || offer.code;
      card.querySelector('[data-card-subheading]').textContent = offer.subheading || '';
      this.applyChipStyle(card.querySelector('[data-card-chip]'), offer.chip);
      card.querySelector('[data-card-savings]').textContent = this.formatSavings(offer);
      card.querySelector('[data-card-code]').textContent = offer.code;

      var copyBtn = card.querySelector('[data-card-copy]');
      var copyLabel = card.querySelector('[data-copy-label]');
      var copyCheck = card.querySelector('[data-copy-check]');
      copyBtn.addEventListener('click', this.onCopy.bind(this, offer.code, copyBtn, copyLabel, copyCheck));

      var applyBtn = card.querySelector('[data-card-apply]');
      var applyLabel = card.querySelector('[data-apply-label]');
      var applyCheck = card.querySelector('[data-apply-check]');
      var isApplied = this.appliedCodes && this.appliedCodes[String(offer.code).toUpperCase()];
      if (isApplied) {
        applyLabel.textContent = window.offerDiscoveryStrings ? window.offerDiscoveryStrings.applied : 'Applied';
        applyCheck.hidden = false;
        applyBtn.classList.add('t4s-offer-discovery__apply--applied');
        applyBtn.disabled = true;
      } else {
        applyBtn.addEventListener('click', this.onApply.bind(this, offer.code, applyBtn, applyLabel, applyCheck));
      }

      this.bindTerms(card, offer);
      return node;
    }

    buildBankOfferCard(offer) {
      var node = this.cardTemplates['bank-offers'].content.cloneNode(true);
      var card = node.querySelector('.t4s-offer-discovery__card');

      card.querySelector('[data-card-title]').textContent = offer.title || offer.bank_name;
      card.querySelector('[data-card-subheading]').textContent = offer.subheading || '';
      this.applyChipStyle(card.querySelector('[data-card-chip]'), offer.chip);
      card.querySelector('[data-card-savings]').textContent = this.formatSavings(offer);
      card.querySelector('[data-card-bank-badge]').textContent = this.bankInitials(offer.bank_name);

      var metaParts = [offer.bank_name];
      if (offer.card_type) metaParts.push(CARD_TYPE_LABELS[offer.card_type] || offer.card_type);
      card.querySelector('[data-card-bank-meta]').textContent = metaParts.filter(Boolean).join(' • ');

      this.bindTerms(card, offer);
      return node;
    }

    bankInitials(name) {
      if (!name) return '?';
      var parts = name.trim().split(/\s+/);
      return (parts[0].charAt(0) + (parts[1] ? parts[1].charAt(0) : '')).toUpperCase();
    }

    formatSavings(offer) {
      if (offer.discount_type === 'percentage' && offer.discount_value != null) {
        var text = offer.discount_value + '% OFF';
        if (offer.max_discount_value) {
          text += ' up to ' + formatMoney(offer.max_discount_value * 100, this.moneyFormat);
        }
        return text;
      }
      if (offer.discount_type === 'fixed_amount' && offer.discount_value != null) {
        return 'Flat ' + formatMoney(offer.discount_value * 100, this.moneyFormat) + ' OFF';
      }
      return '';
    }

    effectiveDiscountCents(offer) {
      if (offer.discount_value == null) return 0;
      var amountRupees;
      if (offer.discount_type === 'percentage') {
        amountRupees = (this.currentPrice / 100) * (offer.discount_value / 100);
        if (offer.max_discount_value) amountRupees = Math.min(amountRupees, offer.max_discount_value);
      } else if (offer.discount_type === 'fixed_amount') {
        amountRupees = offer.discount_value;
      } else {
        return 0;
      }
      return Math.round(amountRupees * 100);
    }

    maybeUpdateTeaser() {
      if (this.data.coupons === null || this.data['bank-offers'] === null) return;

      // Both tabs resolved successfully with zero items between them - not
      // an error (that leaves data[key] as null and never reaches here),
      // genuinely nothing to offer on this product. Hide the whole tile
      // row instead of leaving an "active" Coupons tile with nothing behind
      // it once opened.
      var totalOffers = (this.data.coupons || []).length + (this.data['bank-offers'] || []).length;
      if (totalOffers === 0) {
        this.hidden = true;
        if (this.teaser) this.teaser.hidden = true;
        return;
      }

      if (!this.teaser) return;

      var self = this;
      var best = null;

      (this.data.coupons || []).forEach(function (offer) {
        var amount = self.effectiveDiscountCents(offer);
        if (amount > 0 && (!best || amount > best.amountCents)) {
          best = { amountCents: amount, label: offer.code, tab: 'coupons' };
        }
      });
      // Bank offers stay in the drawer but don't feed the "best price": only
      // coupon codes are applied to the cart automatically (minaki-auto-offer.js),
      // so a bank-offer price here would be one the shopper never actually gets.

      if (!best || best.amountCents >= this.currentPrice) {
        this.teaser.hidden = true;
        if (this.priceStrike) this.priceStrike.hidden = true;
        if (this.priceFinal) this.priceFinal.hidden = true;
        if (this.priceMrp) this.priceMrp.hidden = false;
        return;
      }
      this.renderTeaser(best);
    }

    renderTeaser(best) {
      if (this.priceMrp) this.priceMrp.hidden = true;
      if (this.priceStrike) {
        this.priceStrike.textContent = formatMoney(this.currentPrice, this.moneyFormat);
        // .money is added here (not in Liquid) so the T4S currency converter's
        // initial page-load scan never sees this span while it's still empty/
        // hidden - that would cache a bogus base amount permanently.
        this.priceStrike.classList.add('money');
        this.priceStrike.hidden = false;
      }
      if (this.priceFinal) {
        this.priceFinal.textContent = formatMoney(this.currentPrice - best.amountCents, this.moneyFormat);
        this.priceFinal.classList.add('money');
        this.priceFinal.hidden = false;
      }

      var template = (window.offerDiscoveryStrings && window.offerDiscoveryStrings.teaserCta) ||
        'Applied automatically in your cart with {{ code }}';
      this.teaser.textContent = template.replace('{{ code }}', best.label).replace('{{code}}', best.label);

      var self = this;
      this.teaser.onclick = function () { self.open(best.tab); };
      this.teaser.hidden = false;

      // Re-run T4S's currency conversion pass so the freshly-populated
      // strike/final spans convert immediately instead of waiting for the
      // shopper to manually switch currency again.
      if (window.jQuery_T4NT) {
        window.jQuery_T4NT(document.body).trigger('currency:update');
      }
    }

    onCopy(code, button, labelEl, checkEl) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(code).catch(function () {});
      }
      labelEl.textContent = window.offerDiscoveryStrings ? window.offerDiscoveryStrings.copied : 'Copied';
      checkEl.hidden = false;
      button.classList.add('t4s-offer-discovery__copy--copied');
      this.liveEl.textContent = 'Code ' + code + ' copied to clipboard';

      if (this.copyTimer) clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(function () {
        labelEl.textContent = window.offerDiscoveryStrings ? window.offerDiscoveryStrings.copyCode : 'Copy Code';
        checkEl.hidden = true;
        button.classList.remove('t4s-offer-discovery__copy--copied');
      }, COPY_REVERT_MS);
    }

    onApply(code, applyBtn, applyLabel, applyCheck) {
      // POST /cart/update.js applies the code directly to the cart - no
      // navigation to /discount/{code}. Success is read off the returned
      // cart's discount_codes array (Shopify doesn't error on a bad code,
      // it just doesn't apply it). Fires 'cart:refresh', the same signal
      // the rest of the theme's AJAX cart already listens to after
      // add/remove/qty changes, so header count and cart-page totals stay
      // in sync without this file needing to know about section IDs.
      applyBtn.disabled = true;
      var strings = window.offerDiscoveryStrings || {};
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var codes = cart.discount_codes || [];
          var match = codes.filter(function (dc) {
            return String(dc.code).toUpperCase() === String(code).toUpperCase();
          })[0];
          if (match && match.applicable) {
            applyLabel.textContent = strings.applied || 'Applied';
            applyCheck.hidden = false;
            applyBtn.classList.add('t4s-offer-discovery__apply--applied');
            document.dispatchEvent(new CustomEvent('cart:refresh'));
          } else {
            applyBtn.disabled = false;
            applyLabel.textContent = strings.invalidCode || "Couldn't apply";
          }
        })
        .catch(function () {
          applyBtn.disabled = false;
          applyLabel.textContent = strings.invalidCode || "Couldn't apply";
        });
    }
  }

  if (!customElements.get('offer-discovery-drawer')) {
    customElements.define('offer-discovery-drawer', OfferDiscovery);
  }
})();
